import { useMemo, useEffect, useState } from 'react';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectWorkingProjectRegistrationSettings, selectProjectChannel } from '@/store/slices/project/selectors';
import { fetchPasscodeLists } from '@/store/slices/passcode-lists';
import { generateDefaultRegistrantFilters } from './default-passcode-lists';
import { GateTypes, PasscodeList, RegistrantFilter } from '@/types/registration.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import PasscodeListCard from './components/passcode-lists/passcode-list-card';
import RegistrantFilterCard from './components/listsv2/registrant-filter-card';
import RegistrantFilterCardLarge from './components/listsv2/registrant-filter-card-large';
import {
  selectLoadingPasscodeLists,
  selectMemberPasscodeListsLoading,
  selectPasscodeLists,
} from '@/store/slices/passcode-lists/selectors';
import AudiencesFilters from './components/audiences-filters';
import SelectListModal from './components/listsv2/select-list-modal';
import { useRegistrantStatusCounts } from './hooks/use-registrant-status-counts';
import AudiencesSkeletonLoader from '@ui/skeleton-loader/custom/audiences-skeleton-loader';

const Audiences = () => {
  const dispatch = useAppDispatch();
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const memberPasscodeListsLoading = useAppSelector(selectMemberPasscodeListsLoading);
  const loadingPasscodeLists = useAppSelector(selectLoadingPasscodeLists);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const channel = useAppSelector(selectProjectChannel);

  const [showListsModal, setShowListsModal] = useState(false);
  const [attachedPasscodeLists, setAttachedPasscodeLists] = useState<PasscodeList[]>([]);
  const [unattachedPasscodeLists, setUnattachedPasscodeLists] = useState<PasscodeList[]>([]);
  const [filteredAttachedPasscodeLists, setFilteredAttachedPasscodeLists] = useState<PasscodeList[]>(passcodeLists);
  const [filteredDefaultFilters, setFilteredDefaultFilters] = useState<RegistrantFilter[]>([]);

  const params = useParams();
  const activeView = params['*'];
  const { projectUuid } = params;

  const { statusCounts, loading: statusCountsLoading } = useRegistrantStatusCounts(projectUuid || '');
  const [searchParams] = useSearchParams();
  const isLoading = memberPasscodeListsLoading || loadingPasscodeLists || statusCountsLoading;

  const defaultRegistrantFilters = useMemo(() => {
    return statusCounts ? generateDefaultRegistrantFilters(statusCounts) : [];
  }, [statusCounts]);

  useEffect(() => {
    if (channel) {
      dispatch(fetchPasscodeLists(channel));
    }
  }, [channel, dispatch]);

  useEffect(() => {
    const attachedPasscodeListIds: number[] =
      registrationSettings?.requirements?.[GateTypes.passcode_list]?.flatMap(
        (requirement) => requirement.passcodeList ?? []
      ) ?? [];

    const { attachedLists, unattachedLists } = passcodeLists.reduce(
      (both, list) => {
        if (list.passcode_list && attachedPasscodeListIds.includes(list.passcode_list)) {
          both.attachedLists.push(list);
        } else {
          both.unattachedLists.push(list);
        }
        return both;
      },
      { attachedLists: [] as PasscodeList[], unattachedLists: [] as PasscodeList[] }
    );

    setAttachedPasscodeLists(attachedLists);
    setUnattachedPasscodeLists(unattachedLists);
  }, [passcodeLists, registrationSettings]);

  const search = searchParams.get('search') || '';

  useEffect(() => {
    if (search) {
      const matchedDefaultFilters = defaultRegistrantFilters.filter((filter) =>
        filter.name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredDefaultFilters(matchedDefaultFilters);

      const matchedLists = attachedPasscodeLists.filter((list) => {
        return list.name.toLowerCase().includes(search.toLowerCase());
      });
      setFilteredAttachedPasscodeLists(matchedLists);
    } else {
      setFilteredDefaultFilters(defaultRegistrantFilters);
      setFilteredAttachedPasscodeLists(attachedPasscodeLists);
    }
  }, [attachedPasscodeLists, defaultRegistrantFilters, search]);

  return (
    <>
      <Container
        className="flex-column gap-16"
        minHeight="calc(100vh - 500px)"
        padding="0 var(--main-content-horizontal-padding)"
      >
        <Text tag="h4" className="H4" color="var(--gray-100)">
          Audiences
        </Text>
        <Container
          className="flex ai-flex-start position-absolute z-10 top-0 left-0 right-0 bottom-0"
          opacity={isLoading ? 1 : 0}
          style={{
            visibility: isLoading ? 'visible' : 'hidden',
            transition: 'opacity 300ms ease-in-out, visibility 300ms ease-in-out',
            pointerEvents: isLoading ? 'auto' : 'none',
            position: 'absolute',
            top: 48,
          }}
        >
          <AudiencesSkeletonLoader />
        </Container>
        <Container
          opacity={isLoading ? 0 : 1}
          style={{
            visibility: isLoading ? 'hidden' : 'visible',
            transition: 'opacity 300ms ease-in-out, visibility 300ms ease-in-out',
            pointerEvents: isLoading ? 'none' : 'auto',
          }}
        >
          {filteredDefaultFilters
            .filter((filter) => filter.type === 'all')
            .map((filter) => {
              return <RegistrantFilterCardLarge key={filter.type} filter={filter} />;
            })}
          <Container
            className="grid jc-flex-start ai-center gap-16 radius-8 overflow-hidden"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            }}
          >
            {filteredDefaultFilters
              .filter((filter) => filter.type !== 'all')
              .map((filter) => {
                return <RegistrantFilterCard key={filter.type} filter={filter} />;
              })}
            <OptionalComponent display={filteredAttachedPasscodeLists.length > 0}>
              {filteredAttachedPasscodeLists.map((passcodeList) => {
                return <PasscodeListCard key={passcodeList.passcode_list} passcodeList={passcodeList} />;
              })}
            </OptionalComponent>
          </Container>
        </Container>
        <Container className="position-absolute right-0" style={{ top: '0' }}>
          <AudiencesFilters
            expandedSearchWidth="280px"
            onAddClick={() => setShowListsModal(true)}
            activeView={activeView}
          />
        </Container>
      </Container>
      <SelectListModal open={showListsModal} setOpen={setShowListsModal} passcodeLists={unattachedPasscodeLists} />
    </>
  );
};

export default Audiences;
